import { Box, colors, Dropdown, Typography } from "@procore/core-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { selectDefaultVisibility, selectMainObject, selectPreIsolationVisibility } from "features/render";

import { createBulkActions } from "../../objectTree/utils/bulkActions";

export interface BulkActionsDropdownProps {
    selectedCount: number;
    totalCount: number;
}

export function BulkActionsDropdown({ selectedCount, totalCount }: BulkActionsDropdownProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const mainObject = useAppSelector(selectMainObject);

    const { idArr: currentlyHighlightedIds } = useHighlighted();
    const { ids: hiddenIdsMap, lastHiddenIds } = useHidden();

    const actions = useMemo(
        () =>
            createBulkActions(currentlyHighlightedIds.length, currentlyHighlightedIds, {
                t,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                hiddenIds: hiddenIdsMap,
                lastHiddenIds,
                origin: EventOrigin.PropertiesPanel,
            }),
        [
            currentlyHighlightedIds,
            t,
            dispatch,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            hiddenIdsMap,
            lastHiddenIds,
        ],
    );

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" data-testid="bulk-actions-dropdown">
            <Dropdown
                label={t("actions")}
                variant="tertiary"
                data-testid="bulk-actions-dropdown-button"
                onSelect={({ item }) => {
                    const action = actions.find((a) => a.id === item);
                    if (action && !action.disabled) {
                        action.onClick();
                    }
                }}
            >
                {actions.map((action) => (
                    <Dropdown.Item
                        key={action.id}
                        item={action.id}
                        disabled={action.disabled}
                        data-testid={`bulk-action-${action.id}`}
                    >
                        {action.label}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            <Typography
                data-testid="bulk-actions-count"
                style={{
                    color: colors.gray15,
                }}
            >
                {t("propertiesOfTotal", { selectedCount, totalCount })}
            </Typography>
        </Box>
    );
}
